import type { IndicatorSeries } from "../types.ts";
import type { HousingPolicyBundle } from "./housingPolicyRegimes.ts";
import type { LegacyMortgageStressBundle } from "./legacyMortgageStress.ts";
import { latestValue } from "../utils/series.ts";

export type SituationDirection = "easing" | "tightening" | "mixed" | "neutral" | "na";
export type SignalAgreement = "high" | "medium" | "low" | "na";
export type AnalysisDomain = "market" | "funding" | "loan" | "housing" | "credit" | "real";
export type OutlookHorizon = "3M" | "6M" | "12M";

type SignalEffect = -1 | 0 | 1; // -1 easing/supportive, +1 tightening/stress, 0 neutral

export interface SituationAnalysisSignal {
  id: string;
  label: string;
  domain: AnalysisDomain;
  effect: SignalEffect;
  evidence: string;
  explanation: string;
  stale?: boolean;
}

export interface SituationOutlook {
  horizon: OutlookHorizon;
  direction: SituationDirection;
  agreement: SignalAgreement;
  alignedSignals: number;
  consideredSignals: number;
  headline: string;
  explanation: string;
  drivers: string[];
  counterSignals: string[];
  reversalConditions: string[];
}

export interface SituationChangeSummary {
  direction: SituationDirection;
  agreement: SignalAgreement;
  alignedSignals: number;
  consideredSignals: number;
  label: string;
  headline: string;
  explanation: string;
  highlights: string[];
  counterSignals: string[];
}

export type HistoricalValidationStatus = "ready" | "limited" | "insufficient";

export interface HistoricalDirectionValidation {
  targetId: string;
  targetLabel: string;
  horizon: OutlookHorizon;
  status: HistoricalValidationStatus;
  hitRatePct: number | null;
  hits: number;
  samples: number;
  startMonth: string | null;
  endMonth: string | null;
  note: string;
}

export interface SituationAnalysis {
  current: {
    direction: SituationDirection;
    label: string;
    headline: string;
    explanation: string;
    transmission: string;
    drivers: string[];
    counterSignals: string[];
  };
  recentChange: SituationChangeSummary;
  outlooks: SituationOutlook[];
  historicalValidation: HistoricalDirectionValidation[];
  unknowns: string[];
  method: {
    title: string;
    summary: string;
    guardrails: string[];
  };
  signals: SituationAnalysisSignal[];
}

export interface BuildSituationAnalysisInput {
  policyGap: IndicatorSeries;
  fundingMomentum: IndicatorSeries;
  creditSpread: IndicatorSeries;
  economicSentiment: IndicatorSeries;
  industrialProduction: IndicatorSeries;
  unsoldTotal: IndicatorSeries;
  housing?: HousingPolicyBundle;
  legacyMortgage?: LegacyMortgageStressBundle;
}

function monthKey(date: string) {
  return /^\d{4}-\d{2}/.test(date) ? date.slice(0, 7) : null;
}

function shiftMonth(month: string, delta: number) {
  const [year, mon] = month.split("-").map(Number);
  const date = new Date(Date.UTC(year, mon - 1 + delta, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

function monthlyLastValues(series: IndicatorSeries) {
  const values = new Map<string, number>();
  for (const observation of series.observations) {
    if (observation.value == null || !Number.isFinite(observation.value)) continue;
    const month = monthKey(observation.date);
    if (!month) continue;
    // Observations are chronological in the loaders. The last valid observation
    // within a month becomes that month's comparison point; no missing month is filled.
    values.set(month, observation.value);
  }
  return values;
}

function exactMonthDelta(series: IndicatorSeries, months: number) {
  const values = monthlyLastValues(series);
  const monthsAvailable = [...values.keys()].sort();
  const toMonth = monthsAvailable.at(-1) ?? null;
  if (!toMonth) return null;
  const fromMonth = shiftMonth(toMonth, -months);
  const to = values.get(toMonth);
  const from = values.get(fromMonth);
  if (to == null || from == null) return null;
  return { from, to, delta: to - from, fromMonth, toMonth };
}

function formatNumber(value: number, digits = 2) {
  const rounded = value.toFixed(digits);
  return `${value > 0 ? "+" : ""}${rounded}`;
}

function formatUnit(series: IndicatorSeries) {
  if (series.unit === "%") return "%p";
  if (series.unit === "bp") return "bp";
  if (series.unit === "pp") return "%p";
  if (series.unit === "count") return "건";
  if (series.unit === "housing_units") return "호";
  return series.unit === "index" ? "" : series.unit;
}

function stale(series: IndicatorSeries) {
  return series.source.quality === "stale";
}

function levelSignal(args: {
  id: string;
  label: string;
  domain: AnalysisDomain;
  series: IndicatorSeries;
  positiveMeansTightening: boolean;
  explanationPositive: string;
  explanationNegative: string;
  digits?: number;
}) : SituationAnalysisSignal | null {
  const value = latestValue(args.series);
  if (value == null) return null;
  const effect: SignalEffect = value === 0 ? 0 : (value > 0) === args.positiveMeansTightening ? 1 : -1;
  const unit = formatUnit(args.series);
  return {
    id: args.id,
    label: args.label,
    domain: args.domain,
    effect,
    evidence: `${args.series.shortName} ${formatNumber(value, args.digits ?? 2)}${unit}`,
    explanation: effect === 0 ? "방향 차이가 거의 없습니다." : effect > 0 ? args.explanationPositive : args.explanationNegative,
    stale: stale(args.series)
  };
}

function trendSignal(args: {
  id: string;
  label: string;
  domain: AnalysisDomain;
  series: IndicatorSeries;
  months: number;
  risingMeansTightening: boolean;
  risingText: string;
  fallingText: string;
  digits?: number;
}) : SituationAnalysisSignal | null {
  const trend = exactMonthDelta(args.series, args.months);
  if (!trend) return null;
  const effect: SignalEffect = trend.delta === 0 ? 0 : (trend.delta > 0) === args.risingMeansTightening ? 1 : -1;
  const unit = formatUnit(args.series);
  return {
    id: args.id,
    label: args.label,
    domain: args.domain,
    effect,
    evidence: `${trend.fromMonth}→${trend.toMonth} ${formatNumber(trend.delta, args.digits ?? 2)}${unit}`,
    explanation: trend.delta === 0 ? "해당 기간의 순변화가 거의 없습니다." : trend.delta > 0 ? args.risingText : args.fallingText,
    stale: stale(args.series)
  };
}

function capitalTransactionTrend(housing: HousingPolicyBundle | undefined, months: number): SituationAnalysisSignal | null {
  const rtms = housing?.transactionModule.rtms;
  if (!rtms) return null;
  const rows = rtms.scopes.capital ?? [];
  if (rows.length === 0) return null;

  const declaredComplete = rtms.coverage.completeMonthsByScope?.capital ?? [];
  const complete = new Set(declaredComplete);
  const allowAllRows = rtms.coverage.backfillComplete && complete.size === 0;
  const totals = new Map<string, number>();
  for (const row of rows) {
    if (!allowAllRows && complete.size > 0 && !complete.has(row.month)) continue;
    if (!allowAllRows && complete.size === 0 && !rtms.coverage.backfillComplete) continue;
    totals.set(row.month, (totals.get(row.month) ?? 0) + row.transactionCount);
  }
  const available = [...totals.keys()].sort();
  const toMonth = available.at(-1) ?? null;
  if (!toMonth) return null;
  const fromMonth = shiftMonth(toMonth, -months);
  const from = totals.get(fromMonth);
  const to = totals.get(toMonth);
  if (from == null || to == null || from <= 0) return null;
  const changePct = (to / from - 1) * 100;
  const effect: SignalEffect = changePct === 0 ? 0 : changePct > 0 ? -1 : 1;
  return {
    id: `capital-transactions-${months}m`,
    label: "수도권 아파트 실거래",
    domain: "housing",
    effect,
    evidence: `${fromMonth}→${toMonth} ${formatNumber(changePct, 1)}%`,
    explanation: changePct > 0
      ? "완결월 기준 거래량이 늘어 주택수요 확인 신호는 완화·회복 방향입니다. 가격 상승을 자동 의미하지는 않습니다."
      : changePct < 0
        ? "완결월 기준 거래량이 줄어 주택수요 확인 신호는 약화 방향입니다. 가격 하락을 자동 의미하지는 않습니다."
        : "완결월 기준 거래량의 순변화가 거의 없습니다."
  };
}

function summarizeDirection(signals: SituationAnalysisSignal[]) {
  const usable = signals.filter((signal) => signal.effect !== 0);
  const tightening = usable.filter((signal) => signal.effect > 0).length;
  const easing = usable.filter((signal) => signal.effect < 0).length;
  const considered = usable.length;
  if (considered < 3) {
    return { direction: "na" as const, agreement: "na" as const, tightening, easing, considered, aligned: Math.max(tightening, easing) };
  }
  const aligned = Math.max(tightening, easing);
  const share = aligned / considered;
  if (tightening === easing || share < 0.60) {
    return { direction: "mixed" as const, agreement: "low" as const, tightening, easing, considered, aligned };
  }
  const direction: SituationDirection = tightening > easing ? "tightening" : "easing";
  // This is a display-only agreement grade, not a statistical confidence interval.
  const agreement: SignalAgreement = share >= 0.75 && considered >= 6 ? "high" : "medium";
  return { direction, agreement, tightening, easing, considered, aligned };
}

function directionLabel(direction: SituationDirection) {
  if (direction === "tightening") return "압박 확대 쪽";
  if (direction === "easing") return "완화 쪽";
  if (direction === "mixed") return "신호 엇갈림";
  if (direction === "neutral") return "대체로 중립";
  return "판단 보류";
}

function agreementLabel(agreement: SignalAgreement) {
  if (agreement === "high") return "높음";
  if (agreement === "medium") return "보통";
  if (agreement === "low") return "낮음";
  return "N/A";
}

function signalText(signal: SituationAnalysisSignal) {
  return `${signal.label}: ${signal.explanation} (${signal.evidence})`;
}

function dominantSignals(signals: SituationAnalysisSignal[], direction: SituationDirection, limit = 3) {
  const effect = direction === "tightening" ? 1 : direction === "easing" ? -1 : null;
  if (effect == null) return [];
  return signals.filter((signal) => signal.effect === effect).slice(0, limit).map(signalText);
}

function counterSignals(signals: SituationAnalysisSignal[], direction: SituationDirection, limit = 2) {
  const effect = direction === "tightening" ? -1 : direction === "easing" ? 1 : null;
  if (effect == null) return signals.filter((signal) => signal.effect !== 0).slice(0, limit).map(signalText);
  return signals.filter((signal) => signal.effect === effect).slice(0, limit).map(signalText);
}

function reversalConditions(direction: SituationDirection) {
  if (direction === "tightening") {
    return [
      "시장·조달금리와 신규 주담대금리가 함께 내려가고 신용스프레드까지 축소되면 압박 확대 판단이 약해집니다.",
      "주택심리·실거래·실물지표가 동시에 개선되면 충격 전이가 중단되거나 완화되는 쪽으로 다시 봅니다."
    ];
  }
  if (direction === "easing") {
    return [
      "시장·조달금리와 신규 주담대금리가 다시 오르고 신용스프레드가 확대되면 완화 판단이 약해집니다.",
      "주택심리·실거래·실물지표가 함께 꺾이면 금융여건 개선이 실제경제로 이어지지 않는 것으로 다시 봅니다."
    ];
  }
  return [
    "금리·신용·주택·실물 중 여러 영역이 같은 방향으로 정렬되기 전에는 방향을 강하게 잡지 않습니다.",
    "현재 엇갈림이 해소되는지 다음 관측을 확인합니다."
  ];
}

function buildTrendSignals(input: BuildSituationAnalysisInput, months: number) {
  const legacy = input.legacyMortgage;
  const signals = [
    trendSignal({
      id: `policy-gap-${months}m`, label: "시장금리와 정책금리의 간격", domain: "market", series: input.policyGap, months,
      risingMeansTightening: true,
      risingText: "시장금리가 정책금리보다 상대적으로 더 위로 움직여 금융여건 압박 방향입니다.",
      fallingText: "시장금리와 정책금리의 간격이 줄어 완화 방향입니다.", digits: 1
    }),
    trendSignal({
      id: `funding-${months}m`, label: "은행 신규 조달부담", domain: "funding", series: input.fundingMomentum, months,
      risingMeansTightening: true,
      risingText: "신규 조달비용이 기존 평균보다 더 비싸지는 방향입니다.",
      fallingText: "신규 조달비용과 기존 평균의 격차가 줄어드는 방향입니다.", digits: 1
    }),
    trendSignal({
      id: `credit-${months}m`, label: "기업 신용스프레드", domain: "credit", series: input.creditSpread, months,
      risingMeansTightening: true,
      risingText: "기업이 국채보다 추가로 부담하는 신용 프리미엄이 확대되는 방향입니다.",
      fallingText: "기업 신용 프리미엄이 축소되는 방향입니다.", digits: 1
    }),
    legacy ? trendSignal({
      id: `mortgage-new-${months}m`, label: "신규 주담대금리", domain: "loan", series: legacy.inputs.newMortgageRate, months,
      risingMeansTightening: true,
      risingText: "새로 대출받는 차주의 금융비용이 높아지는 방향입니다.",
      fallingText: "새로 대출받는 차주의 금융비용이 낮아지는 방향입니다."
    }) : null,
    legacy ? trendSignal({
      id: `mortgage-existing-${months}m`, label: "기존 주담대 잔액금리", domain: "housing", series: legacy.inputs.existingMortgageCost, months,
      risingMeansTightening: true,
      risingText: "기존차주의 평균 금리부담이 높아지는 방향입니다.",
      fallingText: "기존차주의 평균 금리부담이 낮아지는 방향입니다."
    }) : null,
    legacy ? trendSignal({
      id: `housing-outlook-${months}m`, label: "주택가격전망 CSI", domain: "housing", series: legacy.inputs.housingPriceOutlook, months,
      risingMeansTightening: false,
      risingText: "주택가격 기대가 높아지는 방향이라 수요심리는 상대적으로 받쳐주는 신호입니다.",
      fallingText: "주택가격 기대가 약해지는 방향이라 수요심리는 둔화 신호입니다.", digits: 1
    }) : null,
    capitalTransactionTrend(input.housing, months),
    trendSignal({
      id: `esi-${months}m`, label: "경제심리지수", domain: "real", series: input.economicSentiment, months,
      risingMeansTightening: false,
      risingText: "경제심리가 개선되는 방향입니다.",
      fallingText: "경제심리가 약해지는 방향입니다.", digits: 1
    }),
    trendSignal({
      id: `production-${months}m`, label: "전산업생산", domain: "real", series: input.industrialProduction, months,
      risingMeansTightening: false,
      risingText: "생산활동이 개선되는 방향입니다.",
      fallingText: "생산활동이 약해지는 방향입니다.", digits: 1
    }),
    trendSignal({
      id: `unsold-${months}m`, label: "전국 미분양", domain: "housing", series: input.unsoldTotal, months,
      risingMeansTightening: true,
      risingText: "미분양이 늘어 주택 재고부담이 커지는 방향입니다.",
      fallingText: "미분양이 줄어 주택 재고부담이 완화되는 방향입니다.", digits: 0
    })
  ];
  return signals.filter((signal): signal is SituationAnalysisSignal => signal != null);
}

function buildCurrentSignals(input: BuildSituationAnalysisInput) {
  const legacy = input.legacyMortgage;
  const signals: Array<SituationAnalysisSignal | null> = [
    levelSignal({
      id: "current-policy-gap", label: "시장금리 위치", domain: "market", series: input.policyGap, positiveMeansTightening: true,
      explanationPositive: "중기 시장금리가 정책금리보다 높은 쪽에 있어 금융여건 압박 신호입니다.",
      explanationNegative: "중기 시장금리가 정책금리보다 낮은 쪽에 있어 완화 기대가 반영된 방향입니다.", digits: 1
    }),
    levelSignal({
      id: "current-funding", label: "은행 신규 조달비용", domain: "funding", series: input.fundingMomentum, positiveMeansTightening: true,
      explanationPositive: "새 금융채 조달비용이 기존 평균보다 높아 누적 조달비용이 따라 올라갈 압력이 있습니다.",
      explanationNegative: "새 조달비용이 기존 평균보다 낮아 조달여건은 완화 방향입니다.", digits: 1
    }),
    legacy ? levelSignal({
      id: "current-lock-in", label: "신규-기존 주담대금리 차이", domain: "loan", series: legacy.lockInSpread, positiveMeansTightening: true,
      explanationPositive: "신규 주담대금리가 기존 잔액금리보다 높아 신규차주 부담과 이동비용이 상대적으로 큰 상태입니다.",
      explanationNegative: "신규 주담대금리가 기존 잔액금리보다 낮아 신규차주 쪽 금융비용 격차는 완화 방향입니다."
    }) : null,
    legacy ? levelSignal({
      id: "current-repricing", label: "기존차주 재가격 압력", domain: "housing", series: legacy.repricingPressure, positiveMeansTightening: true,
      explanationPositive: "기존 주담대 잔액금리 상승분이 변동금리 stock에 전달되는 방향입니다.",
      explanationNegative: "기존 주담대 잔액금리 변화가 기존차주의 이자부담을 낮추는 방향입니다.", digits: 4
    }) : null,
    trendSignal({
      id: "current-credit", label: "기업 신용", domain: "credit", series: input.creditSpread, months: 3,
      risingMeansTightening: true,
      risingText: "최근 3개월 신용스프레드가 넓어져 기업 신용여건은 압박 방향입니다.",
      fallingText: "최근 3개월 신용스프레드가 좁아져 기업 신용여건은 완화 방향입니다.", digits: 1
    }),
    legacy ? trendSignal({
      id: "current-existing-mortgage", label: "기존차주 금리부담", domain: "housing", series: legacy.inputs.existingMortgageCost, months: 3,
      risingMeansTightening: true,
      risingText: "최근 3개월 기존차주의 평균 주담대금리가 올라 부담이 커지는 방향입니다.",
      fallingText: "최근 3개월 기존차주의 평균 주담대금리가 내려 부담이 줄어드는 방향입니다."
    }) : null,
    legacy ? trendSignal({
      id: "current-housing-outlook", label: "주택가격 기대", domain: "housing", series: legacy.inputs.housingPriceOutlook, months: 3,
      risingMeansTightening: false,
      risingText: "최근 3개월 주택가격 기대가 강해져 수요심리는 받쳐주는 방향입니다.",
      fallingText: "최근 3개월 주택가격 기대가 약해져 수요심리는 둔화 방향입니다.", digits: 1
    }) : null,
    capitalTransactionTrend(input.housing, 3),
    trendSignal({
      id: "current-esi", label: "경제심리", domain: "real", series: input.economicSentiment, months: 3,
      risingMeansTightening: false,
      risingText: "최근 3개월 경제심리가 개선되는 방향입니다.",
      fallingText: "최근 3개월 경제심리가 약해지는 방향입니다.", digits: 1
    }),
    trendSignal({
      id: "current-production", label: "생산활동", domain: "real", series: input.industrialProduction, months: 3,
      risingMeansTightening: false,
      risingText: "최근 3개월 생산활동이 개선되는 방향입니다.",
      fallingText: "최근 3개월 생산활동이 약해지는 방향입니다.", digits: 1
    }),
    trendSignal({
      id: "current-unsold", label: "미분양 재고", domain: "housing", series: input.unsoldTotal, months: 3,
      risingMeansTightening: true,
      risingText: "최근 3개월 미분양이 늘어 주택 재고부담이 커지는 방향입니다.",
      fallingText: "최근 3개월 미분양이 줄어 주택 재고부담이 완화되는 방향입니다.", digits: 0
    })
  ];
  return signals.filter((signal): signal is SituationAnalysisSignal => signal != null);
}


function effectFromLevelAtMonth(series: IndicatorSeries, anchorMonth: string, positiveMeansTightening: boolean): SignalEffect | null {
  const value = monthlyLastValues(series).get(anchorMonth);
  if (value == null || value === 0) return value === 0 ? 0 : null;
  return (value > 0) === positiveMeansTightening ? 1 : -1;
}

function effectFromTrendAtMonth(series: IndicatorSeries, anchorMonth: string, months: number, risingMeansTightening: boolean): SignalEffect | null {
  const values = monthlyLastValues(series);
  const current = values.get(anchorMonth);
  const previous = values.get(shiftMonth(anchorMonth, -months));
  if (current == null || previous == null) return null;
  const delta = current - previous;
  if (delta === 0) return 0;
  return (delta > 0) === risingMeansTightening ? 1 : -1;
}

function capitalTransactionEffectAtMonth(housing: HousingPolicyBundle | undefined, anchorMonth: string, months: number): SignalEffect | null {
  const rtms = housing?.transactionModule.rtms;
  if (!rtms) return null;
  const rows = rtms.scopes.capital ?? [];
  const declaredComplete = new Set(rtms.coverage.completeMonthsByScope?.capital ?? []);
  const allowAllRows = rtms.coverage.backfillComplete && declaredComplete.size === 0;
  if (!allowAllRows && declaredComplete.size === 0) return null;
  const totals = new Map<string, number>();
  for (const row of rows) {
    if (!allowAllRows && !declaredComplete.has(row.month)) continue;
    totals.set(row.month, (totals.get(row.month) ?? 0) + row.transactionCount);
  }
  const current = totals.get(anchorMonth);
  const previous = totals.get(shiftMonth(anchorMonth, -months));
  if (current == null || previous == null || previous <= 0) return null;
  if (current === previous) return 0;
  return current > previous ? -1 : 1;
}

function historicalPredictorSignals(input: BuildSituationAnalysisInput, anchorMonth: string, excludedIds = new Set<string>()) {
  const legacy = input.legacyMortgage;
  const rows: Array<{ id: string; effect: SignalEffect | null }> = [
    { id: "policy-gap", effect: effectFromLevelAtMonth(input.policyGap, anchorMonth, true) },
    { id: "funding", effect: effectFromLevelAtMonth(input.fundingMomentum, anchorMonth, true) },
    { id: "credit", effect: effectFromTrendAtMonth(input.creditSpread, anchorMonth, 3, true) },
    { id: "mortgage-new", effect: legacy ? effectFromTrendAtMonth(legacy.inputs.newMortgageRate, anchorMonth, 3, true) : null },
    { id: "mortgage-existing", effect: legacy ? effectFromTrendAtMonth(legacy.inputs.existingMortgageCost, anchorMonth, 3, true) : null },
    { id: "housing-outlook", effect: legacy ? effectFromTrendAtMonth(legacy.inputs.housingPriceOutlook, anchorMonth, 3, false) : null },
    { id: "capital-transactions", effect: capitalTransactionEffectAtMonth(input.housing, anchorMonth, 3) },
    { id: "esi", effect: effectFromTrendAtMonth(input.economicSentiment, anchorMonth, 3, false) },
    { id: "production", effect: effectFromTrendAtMonth(input.industrialProduction, anchorMonth, 3, false) },
    { id: "unsold", effect: effectFromTrendAtMonth(input.unsoldTotal, anchorMonth, 3, true) },
    { id: "lock-in", effect: legacy ? effectFromLevelAtMonth(legacy.lockInSpread, anchorMonth, true) : null },
    { id: "repricing", effect: legacy ? effectFromLevelAtMonth(legacy.repricingPressure, anchorMonth, true) : null }
  ];
  return rows
    .filter((row) => !excludedIds.has(row.id) && row.effect != null)
    .map((row) => ({
      id: `history-${row.id}-${anchorMonth}`,
      label: row.id,
      domain: "real" as const,
      effect: row.effect!,
      evidence: anchorMonth,
      explanation: "historical validation input"
    } satisfies SituationAnalysisSignal));
}

function futureTargetEffect(series: IndicatorSeries, anchorMonth: string, horizonMonths: number): SignalEffect | null {
  const values = monthlyLastValues(series);
  const current = values.get(anchorMonth);
  const future = values.get(shiftMonth(anchorMonth, horizonMonths));
  if (current == null || future == null) return null;
  if (future === current) return 0;
  return future > current ? -1 : 1;
}

function buildHistoricalValidationForTarget(args: {
  input: BuildSituationAnalysisInput;
  target: IndicatorSeries;
  targetId: string;
  targetLabel: string;
  excludedSignalId: string;
  horizon: OutlookHorizon;
}): HistoricalDirectionValidation {
  const months = Number.parseInt(args.horizon, 10);
  const targetValues = monthlyLastValues(args.target);
  const anchors = [...targetValues.keys()].sort();
  let hits = 0;
  let samples = 0;
  let startMonth: string | null = null;
  let endMonth: string | null = null;

  for (const anchorMonth of anchors) {
    const targetEffect = futureTargetEffect(args.target, anchorMonth, months);
    if (targetEffect == null || targetEffect === 0) continue;
    const predictorSignals = historicalPredictorSignals(args.input, anchorMonth, new Set([args.excludedSignalId]));
    const summary = summarizeDirection(predictorSignals);
    if (summary.direction !== "tightening" && summary.direction !== "easing") continue;
    const predictedEffect: SignalEffect = summary.direction === "tightening" ? 1 : -1;
    samples += 1;
    if (predictedEffect === targetEffect) hits += 1;
    startMonth ??= anchorMonth;
    endMonth = anchorMonth;
  }

  const status: HistoricalValidationStatus = samples >= 24 ? "ready" : samples >= 12 ? "limited" : "insufficient";
  return {
    targetId: args.targetId,
    targetLabel: args.targetLabel,
    horizon: args.horizon,
    status,
    hitRatePct: samples >= 12 ? Number((hits / samples * 100).toFixed(1)) : null,
    hits,
    samples,
    startMonth,
    endMonth,
    note: status === "insufficient"
      ? "비교 가능한 과거 표본이 12개 미만이라 방향 일치율을 공개하지 않습니다."
      : status === "limited"
        ? "표본이 12~23개인 탐색 결과입니다. 예측확률로 해석하지 않습니다."
        : "관측월 기준 사후검증입니다. 당시 발표시점 데이터 빈티지·개정 전 값은 재현하지 않았으므로 미래 예측확률로 해석하지 않습니다."
  };
}

function buildHistoricalValidation(input: BuildSituationAnalysisInput): HistoricalDirectionValidation[] {
  const results: HistoricalDirectionValidation[] = [];
  const targets = [
    { target: input.economicSentiment, targetId: "economic-sentiment", targetLabel: "경제심리지수", excludedSignalId: "esi" },
    { target: input.industrialProduction, targetId: "industrial-production", targetLabel: "전산업생산", excludedSignalId: "production" }
  ];
  for (const target of targets) {
    for (const horizon of ["3M", "6M", "12M"] as OutlookHorizon[]) {
      results.push(buildHistoricalValidationForTarget({ input, ...target, horizon }));
    }
  }
  return results;
}

function buildRecentChange(input: BuildSituationAnalysisInput): SituationChangeSummary {
  const signals = buildTrendSignals(input, 1);
  const summary = summarizeDirection(signals);
  const highlights = dominantSignals(signals, summary.direction, 4);
  const counters = counterSignals(signals, summary.direction, 3);
  const label = summary.direction === "tightening"
    ? "압박 쪽으로 이동"
    : summary.direction === "easing"
      ? "완화 쪽으로 이동"
      : summary.direction === "mixed"
        ? "엇갈림"
        : "판단 보류";
  const headline = summary.direction === "tightening"
    ? "직전 비교월보다 압박 방향으로 움직인 지표가 더 많습니다."
    : summary.direction === "easing"
      ? "직전 비교월보다 완화·회복 방향으로 움직인 지표가 더 많습니다."
      : summary.direction === "mixed"
        ? "직전 비교월 대비 변화 방향이 서로 갈려 뚜렷한 한쪽 이동은 아닙니다."
        : "직전 비교월과 정확히 비교할 수 있는 지표가 아직 부족합니다.";
  return {
    direction: summary.direction,
    agreement: summary.agreement,
    alignedSignals: summary.aligned,
    consideredSignals: summary.considered,
    label,
    headline,
    explanation: summary.considered > 0
      ? `정확히 한 달 전 관측이 있는 유효 신호 ${summary.considered}개 중 ${summary.aligned}개가 같은 방향입니다. 방문기록 비교가 아니라 최신 관측과 직전 비교 가능한 월의 변화입니다.`
      : "결측월을 채우지 않기 때문에 정확한 전월 비교가 없으면 변화 판단을 보류합니다.",
    highlights,
    counterSignals: counters
  };
}

function transmissionText(signals: SituationAnalysisSignal[]) {
  const tighteningDomains = new Set(signals.filter((signal) => signal.effect > 0).map((signal) => signal.domain));
  if (tighteningDomains.has("real")) {
    return "금융·대출에서 시작한 압박과 같은 방향의 신호가 실물 확인층에서도 일부 보입니다. 다만 이것만으로 경기침체를 판정하지 않습니다.";
  }
  if (tighteningDomains.has("housing") || tighteningDomains.has("credit")) {
    return "압박 신호가 시장·조달 단계에만 머물지 않고 주택 또는 기업신용 단계까지 일부 이어져 있습니다. 실물까지 같은 방향인지 별도로 확인합니다.";
  }
  if (tighteningDomains.has("funding") || tighteningDomains.has("loan") || tighteningDomains.has("market")) {
    return "현재 압박은 주로 시장금리·은행조달·대출 단계에서 보입니다. 주택·신용·실물이 같은 방향으로 확인되는지는 아직 제한적입니다.";
  }
  return "현재 데이터에서는 금융충격이 아래 단계로 뚜렷하게 번지는 패턴이 확인되지 않습니다.";
}

function currentHeadline(direction: SituationDirection, drivers: string[], counters: string[]) {
  if (direction === "tightening") {
    return counters.length > 0
      ? "금융·실물 압박 신호가 조금 더 우세하지만, 반대 방향 신호도 함께 있습니다."
      : "현재는 금융·실물 압박 신호가 비교적 같은 방향으로 모여 있습니다.";
  }
  if (direction === "easing") {
    return counters.length > 0
      ? "완화·회복 신호가 조금 더 우세하지만, 아직 남아 있는 압박 신호도 있습니다."
      : "현재는 완화·회복 신호가 비교적 같은 방향으로 모여 있습니다.";
  }
  if (direction === "mixed") return "금리·신용·주택·실물 신호가 서로 엇갈려 한 방향으로 단정하기 어렵습니다.";
  if (direction === "na") return "현재 국면을 한 문장으로 묶기에는 유효 데이터가 부족합니다.";
  return drivers[0] ?? "현재는 뚜렷한 한 방향보다 중립에 가깝습니다.";
}

function outlookExplanation(horizon: OutlookHorizon, summary: ReturnType<typeof summarizeDirection>) {
  const lookback = horizon === "3M" ? "최근 3개월" : horizon === "6M" ? "최근 6개월" : "최근 12개월";
  const caution = horizon === "12M"
    ? " 12개월은 정책·대외충격이 끼어들 시간이 길어 방향 참고용으로만 봅니다."
    : "";
  if (summary.direction === "na") return `${lookback}의 정확히 비교 가능한 관측이 부족해 방향을 보류합니다.${caution}`;
  if (summary.direction === "mixed") return `${lookback} 움직임을 같은 기준으로 비교했지만 완화와 압박 신호가 비슷하게 갈립니다.${caution}`;
  return `${lookback} 움직임을 그대로 연장해 보는 기계적 방향성입니다. ${summary.aligned}개 신호가 같은 방향이고, 미래값을 숫자로 예측하는 모델은 아닙니다.${caution}`;
}

function buildOutlook(input: BuildSituationAnalysisInput, horizon: OutlookHorizon): SituationOutlook {
  const months = Number.parseInt(horizon, 10);
  const signals = buildTrendSignals(input, months);
  const summary = summarizeDirection(signals);
  const drivers = dominantSignals(signals, summary.direction, 3);
  const counters = counterSignals(signals, summary.direction, 2);
  return {
    horizon,
    direction: summary.direction,
    agreement: summary.agreement,
    alignedSignals: summary.aligned,
    consideredSignals: summary.considered,
    headline: directionLabel(summary.direction),
    explanation: outlookExplanation(horizon, summary),
    drivers,
    counterSignals: counters,
    reversalConditions: reversalConditions(summary.direction)
  };
}

export function buildSituationAnalysis(input: BuildSituationAnalysisInput): SituationAnalysis {
  const currentSignals = buildCurrentSignals(input);
  const recentChange = buildRecentChange(input);
  const historicalValidation = buildHistoricalValidation(input);
  const currentSummary = summarizeDirection(currentSignals);
  const drivers = dominantSignals(currentSignals, currentSummary.direction, 4);
  const counters = counterSignals(currentSignals, currentSummary.direction, 3);
  const unknowns: string[] = [];

  const legacy = input.legacyMortgage;
  if (legacy?.inputs.mortgageBalance.source.quality === "unavailable") {
    unknowns.push("동일 모집단의 예금은행 주담대 총잔액(Gate C)이 없어 전체 이자부담 규모와 Cash-flow Stress는 계산하지 않습니다.");
  }
  const marketCoverage = input.housing?.transactionModule.rtms?.marketAnalysis?.coverage;
  if (marketCoverage && !marketCoverage.backfillComplete) {
    unknowns.push(`고가시장 세부 RTMS backfill은 진행 중입니다 (${marketCoverage.completedCombinations}/${marketCoverage.expectedCombinations}). 종합 방향판정에는 이 미완결 세부자료를 사용하지 않습니다.`);
  }
  const staleLabels = [...new Set(currentSignals.filter((signal) => signal.stale).map((signal) => signal.label))];
  if (staleLabels.length > 0) unknowns.push(`일부 입력이 STALE입니다: ${staleLabels.join(", ")}. 값은 표시하되 신선도 경고를 유지합니다.`);

  return {
    current: {
      direction: currentSummary.direction,
      label: directionLabel(currentSummary.direction),
      headline: currentHeadline(currentSummary.direction, drivers, counters),
      explanation: currentSummary.direction === "na"
        ? "데이터가 충분히 모일 때까지 N/A를 유지합니다."
        : `현재 유효 신호 ${currentSummary.considered}개 중 ${currentSummary.aligned}개가 주된 방향과 일치합니다. 이 숫자는 통계적 확률이 아니라 단순한 신호 일치도입니다.`,
      transmission: transmissionText(currentSignals),
      drivers,
      counterSignals: counters
    },
    recentChange,
    outlooks: [buildOutlook(input, "3M"), buildOutlook(input, "6M"), buildOutlook(input, "12M")],
    historicalValidation,
    unknowns,
    method: {
      title: "어떻게 읽나요?",
      summary: "각 지표의 최신 수준과 3·6·12개월 실제 변화 방향을 같은 기준으로 모아, 서로 같은 방향인지 확인합니다.",
      guardrails: [
        "미래 금리·집값·경기 수치를 직접 예측하지 않습니다.",
        "신호 일치도는 예측확률이나 신뢰구간이 아닙니다.",
        "결측월을 보간하지 않고 정확히 비교 가능한 월만 사용합니다.",
        "환율처럼 방향의 경제적 의미가 상황에 따라 달라지는 지표는 첫 단계 점수에서 제외합니다.",
        "공식 데이터 Gate가 부족하면 N/A로 남깁니다.",
        "과거 방향검증은 관측월 기준 사후검증이며 당시 발표시점 빈티지와 데이터 개정 이력은 재현하지 않습니다."
      ]
    },
    signals: currentSignals
  };
}

export function situationAgreementLabel(value: SignalAgreement) {
  return agreementLabel(value);
}
